import React, { useState } from 'react';
import { cn } from '../../utils/cn';

/**
 * App-wide image with a "blur-up" load effect (same look as the hero photos).
 *
 *   - While loading: blurred + slightly scaled so the blur edge stays hidden
 *   - When loaded:   sharp + crossfaded in
 *
 * Two modes:
 *   1. Standalone (default) — fades in as soon as the image decodes.
 *      Used for category cards, event cards, destination tiles, etc.
 *
 *   2. Controlled visibility — pass `active={false}` to keep the image hidden
 *      even after load. Used by the hero's slide stack where multiple images
 *      are preloaded but only one is shown at a time.
 *
 * Any extra `<img>` attribute (sizes, srcSet, onError…) is forwarded.
 */
export default function FadeInImage({
  src,
  alt,
  className,
  active,
  eager,
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const isControlled = typeof active === 'boolean';
  // Standalone mode: as soon as it loads, it's "active".
  const visible = isControlled ? (loaded && active) : loaded;

  return (
    <img
      {...rest}
      src={src}
      alt={alt}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      onLoad={(e) => {
        setLoaded(true);
        if (rest.onLoad) rest.onLoad(e);
      }}
      className={cn(
        className,
        'transition-[opacity,filter,transform] duration-700 ease-out',
        loaded ? 'blur-0 scale-100' : 'blur-xl scale-105',
        visible ? 'opacity-100' : 'opacity-0'
      )}
    />
  );
}
